import { createContext, useContext } from "react";
import type { Space, SpaceSection } from "@al-life/types";

/**
 * The space the current `/:slug/...` route belongs to. Provided by SpaceProvider
 * (which loads `GET /spaces/{slug}`) and consumed by feature hooks/components:
 *
 * - `slug` — the URL segment; passed to every space-scoped API call.
 * - `canEdit` — is the viewer a member of THIS space? Gates edit controls
 *   (`<CanEdit>`), NOT app-wide admin. An app admin is not automatically a member.
 * - `isPublicSection(section)` — is that section shared publicly?
 * - `space` — the full loaded space (name, publicSections…), or null while loading.
 * - `reload` — refetch after a mutation (e.g. toggling a section public).
 */
export interface SpaceContextValue {
  slug: string;
  space: Space | null;
  canEdit: boolean;
  isPublicSection: (section: SpaceSection) => boolean;
  reload: () => Promise<void>;
}

export const SpaceContext = createContext<SpaceContextValue | null>(null);

/** Read the current space. Throws if used outside a SpaceProvider (a bug). */
export function useSpaceContext(): SpaceContextValue {
  const ctx = useContext(SpaceContext);
  if (!ctx) throw new Error("useSpaceContext must be used within a SpaceProvider");
  return ctx;
}
